import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Stack } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';

import { caloTheme } from '../../assets/themes/calo';
import { RoutePaths } from '../../libs';
import { useDocumentMedia } from '../../libs/utils/document';
import { formatDate, KitchenShortCut } from '../../libs/utils/helperFunctions';
import { onRoute } from '../../libs/utils/onRoute';

interface TopBarProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  kitchen: string;
  date: string;
  pageName?: string;
  openFilters: boolean;
  setOpenFilters: (value: boolean) => void;
}

const isChefRoute = (pathname: string): boolean =>
  onRoute(pathname, RoutePaths.home) || onRoute(pathname, RoutePaths.videoReview, { nested: true });

const TopBar = ({ openFilters, setOpenFilters, setIsOpen, isOpen, kitchen, date }: TopBarProps) => {
  const pathname = usePathname();

  const { isMobile, isTablet } = useDocumentMedia();
  const pageName = useMemo(() => {
    if (onRoute(pathname, RoutePaths.componentsCheck)) {
      return 'Components Check';
    }
    if (onRoute(pathname, RoutePaths.componentsPortioning)) {
      return 'Components Portioning';
    }
    if (onRoute(pathname, RoutePaths.portioningDisplay)) {
      return 'Meals Portioning';
    }
    if (onRoute(pathname, RoutePaths.mealsStatus)) {
      return 'Meals Status';
    }
    if (onRoute(pathname, RoutePaths.wastageTracking)) {
      return 'Wastage tracking';
    }
    if (onRoute(pathname, RoutePaths.yieldTracking)) {
      return 'Yield tracking';
    }
    if (onRoute(pathname, RoutePaths.shortageReporting)) {
      return 'Shortage Reporting';
    }
    if (onRoute(pathname, RoutePaths.shortageDisplay)) {
      return 'Shortage Display';
    }
    if (onRoute(pathname, RoutePaths.assemblyCheck)) {
      return 'Assembly Check';
    }
    if (onRoute(pathname, RoutePaths.qualityTracking)) {
      return 'Quality Tracking';
    }
    if (onRoute(pathname, RoutePaths.preDispatchCheck)) {
      return 'Pre-Dispatch Check';
    }
    if (onRoute(pathname, RoutePaths.recipeGuideBook)) {
      return 'Recipe GuideBook';
    }
    if (onRoute(pathname, RoutePaths.lockTimeChanges)) {
      return 'Kitchen Requests';
    }
    if (onRoute(pathname, RoutePaths.logisticsChanges)) {
      return 'Logistics Requests';
    }
    if (onRoute(pathname, RoutePaths.driverDispatch)) {
      return 'Driver Dispatch';
    }
    if (onRoute(pathname, RoutePaths.fileManagement)) {
      return 'Files Requests';
    }
    if (onRoute(pathname, RoutePaths.specialRequest)) {
      return 'Special Requests';
    }
    if (onRoute(pathname, RoutePaths.qualityMetrics)) {
      return 'Quality Metrics';
    }
    if (onRoute(pathname, RoutePaths.productivityMetrics)) {
      return 'Productivity Metrics';
    }

    if (onRoute(pathname, RoutePaths.leftoverMetrics)) {
      return 'Leftover Metrics';
    }
    if (onRoute(pathname, RoutePaths.foodPortioningRecord)) {
      return 'Food Portioning Record';
    }
    if (onRoute(pathname, RoutePaths.controlTower)) {
      return 'Control Tower';
    }
    if (onRoute(pathname, RoutePaths.productionPlan)) {
      return 'Production plan';
    }
    if (onRoute(pathname, RoutePaths.batchTracking, { nested: true })) {
      return 'Batch Tracking';
    }
    if (onRoute(pathname, RoutePaths.home)) {
      return 'Today';
    }
    if (onRoute(pathname, RoutePaths.videoReview, { nested: true })) {
      return 'Review clip';
    }
    return '';
  }, [pathname]);
  const showsKitchenDay = !isChefRoute(pathname);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        color="inherit"
        sx={{
          width: { sm: isOpen ? `calc(100% - ${260}px)` : '100%' },
          ml: { sm: isOpen ? '260px' : '0' },
          boxShadow: 0
        }}
      >
        <Toolbar>
          <IconButton size="medium" edge="start" color="inherit" aria-label="menu" onClick={() => setIsOpen(!isOpen)}>
            <SvgIcon
              sx={{ fill: 'none', width: '32px', height: '32px', ml: isMobile ? '-8px' : '' }}
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 24H28" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 16H28" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 8H28" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </SvgIcon>
          </IconButton>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%'
            }}
          >
            {isMobile || isTablet ? (
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    flexGrow: 0,
                    ml: '-4px',
                    fontFamily: caloTheme.typography.fontFamily,
                    lineHeight: '14px',
                    fontWeight: 600,
                    color: caloTheme.palette.neutral900,
                    fontSize: '12px',
                    textTransform: 'uppercase'
                  }}
                >
                  {pageName}
                </Typography>
                {showsKitchenDay && (
                  <Typography
                    variant="h1"
                    sx={{
                      flexGrow: 0,
                      justifyContent: 'end',
                      fontFamily: caloTheme.typography.fontFamily,
                      lineHeight: '14px',
                      fontWeight: 600,
                      color: caloTheme.palette.neutral900,
                      fontSize: '12px',
                      textTransform: 'uppercase'
                    }}
                  >
                    {KitchenShortCut(kitchen)} Kitchen, {formatDate(date, 'dd-MM-yyyy')}
                  </Typography>
                )}
              </Stack>
            ) : (
              <>
                <Typography
                  variant="h1"
                  component="div"
                  sx={{
                    flexGrow: 1,
                    fontFamily: caloTheme.typography.fontFamily,
                    lineHeight: '24px',
                    fontWeight: 400,
                    color: caloTheme.palette.neutral900,
                    fontSize: '24px',
                    textTransform: 'capitalize'
                  }}
                >
                  {pageName}
                </Typography>
                {showsKitchenDay && (
                  <Typography
                    variant="h1"
                    component="div"
                    sx={{
                      flexGrow: 0,
                      fontFamily: caloTheme.typography.fontFamily,
                      lineHeight: '24px',
                      fontWeight: 400,
                      color: caloTheme.palette.neutral900,
                      fontSize: '24px',
                      textTransform: 'capitalize'
                    }}
                  >
                    {kitchen.toUpperCase()} Kitchen, {formatDate(date, 'dd-MM-yyyy')}
                  </Typography>
                )}
              </>
            )}
          </Stack>
          {isMobile &&
            (onRoute(pathname, RoutePaths.mealsStatus) ||
              onRoute(pathname, RoutePaths.assemblyCheck) ||
              onRoute(pathname, RoutePaths.portioningDisplay)) && (
              <IconButton
                sx={{
                  backgroundColor: caloTheme.palette.primary100,
                  width: '50px',
                  height: '36px',
                  mr: '-10px',
                  ml: '2px',
                  borderRadius: '8px',
                  ':hover': {
                    backgroundColor: caloTheme.palette.primary100
                  },
                  ':focus': {
                    backgroundColor: caloTheme.palette.primary100
                  }
                }}
                size="small"
                aria-label="menu"
                onClick={() => setOpenFilters(!openFilters)}
              >
                <SettingsOutlinedIcon style={{ width: '20px', stroke: caloTheme.palette.primary400 }} color={'primary'} />
              </IconButton>
            )}
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default TopBar;
